import { FC, ReactElement, useCallback, useEffect, useRef, useState, UIEvent } from 'react';
import { formatDistanceToNow } from '../../../../utils/format/formatDistanceToNow';
import { formatText } from '../../../../utils/format/formatText';
import { useIsMobile } from '../../../../utils/getIsMobile';
import { makeTransition } from '../../../../utils/styles/makeTransition';
import { useTranslation } from '../../../../utils/useTranslation';
import { IconKebab } from '../../../icon/IconKebab';
import { IconButton } from '../../../input/IconButton';
import { Box } from '../../../layout/Box';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { Flex } from '../../../layout/Flex';
import { Typography } from '../../../typography/Typography';
import { UserUI } from '../../../users/types';
import { PostAILabel } from '../../PostAILabel/PostAILabel';
import { PostUI, ProductItemUI } from '../../types';
import { PostDetailsCardProps, usePostDetailsCardContext } from '../PostDetailsCard';
import { POST_TEXT_SCROLL_OFFET } from '../constants';
import { TextContainer, TextContent, TextFooter } from '../styles';
import { PostProducts } from './PostProducts/PostProducts';
import { PostTextCatalogLink } from './PostTextCatalogLink/PostTextCatalogLink';
import { PostTextControls } from './PostTextControls';
import { PostTextProfile } from './PostTextProfile';
import { PostTextSiteLink } from './PostTextSiteLink/PostTextSiteLink';

interface PostTextProps {
    post: PostUI;
    index: number;
    user?: UserUI;
    onClickOutClick: PostDetailsCardProps['onClickOutClick'];
    onProductButtonClick: PostDetailsCardProps['onProductButtonClick'];
    onPostTextProductsClick: (product: ProductItemUI) => void;
    onPostTextCatalogClick: VoidFunction;
    onPostTextClickoutClick: VoidFunction;
    onReaction: PostDetailsCardProps['onReaction'];
    onBookmark?: PostDetailsCardProps['onBookmark'];
    onShare?: PostDetailsCardProps['onShare'];
    onSubscribe?: PostDetailsCardProps['onSubscribe'];
    renderMenu: (trigger: ReactElement) => ReactElement | null;
}

const KebabTrigger = (
    <Box
        transition={makeTransition({
            styles: ['opacity'],
            ms: 200,
        })}
    >
        <IconButton cursor={'pointer'}>
            <IconKebab
                fontSize={24}
                color="var(--color-primary)"
                dataTestId={DataTestId.POST_CARD_KEBAB}
            />
        </IconButton>
    </Box>
);

const CUT_BREAKPOINT_HEIGHT = 44;
const CUT_TEXT_CONTAINER_MAX_HEIGHT = 54;

export const PostText: FC<PostTextProps> = ({
    post,
    index,
    user,
    onPostTextProductsClick,
    onPostTextCatalogClick,
    onPostTextClickoutClick,
    onReaction,
    onBookmark,
    onShare,
    onSubscribe,
    renderMenu,
}) => {
    const { createdAt, likes, text, products } = post;
    const { sizes } = usePostDetailsCardContext();
    const t = useTranslation();
    const isMobile = useIsMobile();
    const [isCut, setIsCut] = useState(true);
    const textRef = useRef<HTMLDivElement>(null);

    const onCutClick = useCallback(() => {
        setIsCut(false);
    }, []);

    const [topDivider, setTopDivider] = useState(false);
    const [bottomDivider, setBottomDivider] = useState(false);

    const textContainerRef = useRef<HTMLDivElement>(null);
    const textContentRef = useRef<HTMLDivElement>(null);

    const onScrollTextContainer = useCallback((event: UIEvent<HTMLDivElement>) => {
        const scrollContainer = event.currentTarget;
        const contentHeight = textContentRef.current?.clientHeight || 0;
        const scrollTop = scrollContainer.scrollTop;

        setTopDivider(scrollTop > POST_TEXT_SCROLL_OFFET);
        setBottomDivider(
            contentHeight > 0 &&
                scrollTop + scrollContainer.clientHeight < contentHeight - POST_TEXT_SCROLL_OFFET,
        );
    }, []);

    useEffect(() => {
        const textContainerHeight = textContainerRef.current?.clientHeight || 0;
        const textContentHeight = textContentRef.current?.clientHeight || 0;

        setBottomDivider(textContainerHeight < textContentHeight);
    }, []);

    useEffect(() => {
        if (textRef.current && textRef.current?.offsetHeight <= CUT_BREAKPOINT_HEIGHT) {
            setIsCut(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textRef.current]);

    if (sizes.height === 0) {
        return null;
    }

    if (isMobile) {
        return (
            <>
                <TextContainer
                    isMobile
                    maxHeight={isCut ? CUT_TEXT_CONTAINER_MAX_HEIGHT : undefined}
                    height="auto"
                >
                    <Flex direction={'column'} height={'100%'}>
                        <TextContent isMobile dataTestId={DataTestId.POST_TEXT} isCut={isCut}>
                            <span ref={textRef}>{formatText(text)}</span>
                        </TextContent>
                    </Flex>
                    {isCut ? (
                        <Typography
                            onClick={onCutClick}
                            variant="body2"
                            weight="medium"
                            mt={3.25}
                            mr={2}
                        >
                            {t('ещё')}
                        </Typography>
                    ) : undefined}
                </TextContainer>
                <TextContainer isMobile>
                    <Flex direction="column" width="100%" px={2}>
                        <TextFooter dataTestId={DataTestId.POST_TEXT_FOOTERT}>
                            <Box mr={1.5}>{formatDistanceToNow(createdAt)}</Box>
                            {likes > 10 && (
                                <Box display="inline" as="span" mr={1.5}>
                                    {t('Понравилось: ')}
                                    {likes}
                                </Box>
                            )}
                            {post.isAIContent ? (
                                <PostAILabel
                                    spacing={0.5}
                                    iconHeight={12}
                                    iconWidth={12}
                                    text={t('Cоздано с помощью ИИ')}
                                />
                            ) : null}
                        </TextFooter>
                    </Flex>
                </TextContainer>
            </>
        );
    }

    return (
        <TextContainer isMobile={false} height={sizes.height}>
            <Flex direction={'column'} height={'100%'}>
                <Flex
                    width="100%"
                    pr={1}
                    pl={2}
                    py={1}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <PostTextProfile
                        user={user}
                        post={post}
                        index={index}
                        onSubscribe={onSubscribe}
                        width="100%"
                    />
                </Flex>
                <TextContent
                    dataTestId={DataTestId.POST_TEXT}
                    isMobile={false}
                    onScroll={onScrollTextContainer}
                    borderTop={topDivider ? '1px solid var(--divider)' : undefined}
                    borderBottom={bottomDivider ? '1px solid var(--divider)' : undefined}
                    ref={textContainerRef}
                >
                    <Box ref={textContentRef}>
                        <PostProducts
                            products={products}
                            onPostTextProductsClick={onPostTextProductsClick}
                            post={post}
                            onPostTextClickoutClick={onPostTextClickoutClick}
                            onPostTextCatalogClick={onPostTextCatalogClick}
                        />
                        {products.items.length === 0 && (
                            <Flex direction="column" width="100%" spacing={1} py={1}>
                                <PostTextSiteLink
                                    post={post}
                                    text={t('Перейти на сайт')}
                                    onPostTextClickoutClick={onPostTextClickoutClick}
                                />
                                <PostTextCatalogLink
                                    post={post}
                                    onPostTextCatalogClick={onPostTextCatalogClick}
                                />
                            </Flex>
                        )}
                        <Box py={2}>{formatText(text)}</Box>
                    </Box>
                </TextContent>
                <Flex direction="column" width="100%" px={1}>
                    <Flex direction="row" justifyContent="space-between" width="100%" py={1}>
                        <PostTextControls
                            post={post}
                            index={index}
                            onReaction={onReaction}
                            onBookmark={onBookmark}
                            onShare={onShare}
                        />
                        <Box alignSelf="center">{renderMenu(KebabTrigger)}</Box>
                    </Flex>
                    <TextFooter dataTestId={DataTestId.POST_TEXT_FOOTERT} px={1}>
                        <Box mr={2}>{formatDistanceToNow(createdAt)}</Box>
                        {likes > 10 && (
                            <Box display="inline" as="span" mr={2}>
                                {t('Понравилось: ')}
                                {likes}
                            </Box>
                        )}
                        {post.isAIContent ? (
                            <PostAILabel
                                spacing={0.5}
                                iconHeight={12}
                                iconWidth={12}
                                text={t('Cоздано с помощью ИИ')}
                            />
                        ) : null}
                    </TextFooter>
                </Flex>
            </Flex>
        </TextContainer>
    );
};
